import { useEffect, useState } from 'react';

const THEME_STORAGE_KEY = '@snap-reports-theme';
const DARK_CLASS = 'dark';

type Theme = 'light' | 'dark';

const getStoredTheme = (): Theme | null => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
  } catch {
    return null;
  }
};

const setStoredTheme = (theme: Theme): void => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (error) {
    console.warn('Failed to save theme preference:', error);
  }
};

export const useTheme = () => {
  const [isDark, setIsDarkState] = useState<boolean>(() => {
    const storedTheme = getStoredTheme();

    if (storedTheme) {
      return storedTheme === 'dark';
    }

    // Fallback to browser preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Add theme-switching class to prevent color blink
    document.documentElement.classList.add('theme-switching');

    // Apply theme class to document
    if (isDark) {
      document.documentElement.classList.add(DARK_CLASS);
    } else {
      document.documentElement.classList.remove(DARK_CLASS);
    }

    // Persist theme preference on every change
    setStoredTheme(isDark ? 'dark' : 'light');

    // Remove theme-switching class after transition
    const timeout = setTimeout(() => {
      document.documentElement.classList.remove('theme-switching');
    }, 50);

    return () => clearTimeout(timeout);
  }, [isDark]);

  const setIsDark = (value: boolean) => {
    setIsDarkState(value);
  };

  return { isDark, setIsDark };
};
